import { useState } from 'react';
import {
    eachDayOfInterval,
    eachMonthOfInterval,
    format,
    parseISO,
} from 'date-fns';
import {
    Alert,
    Box,
    Card,
    Checkbox,
    CircularProgress,
    FormControlLabel,
    MenuItem,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography,
} from '@mui/material';
import { LineChart } from '@mui/x-charts/LineChart';
import { useCanAccess } from '@react-admin/ra-rbac';
import { Title, useTranslate } from 'react-admin';
import {
    confirmationDefaults,
    confirmationRoles,
    ConfirmationFilter,
    ConfirmationSummary,
    useDataConfirmations,
} from './statistics/DataConfirmations';
import { ConfirmationRole } from './statistics/types';
import { numberFormatter } from './statistics/common';

const DataConfirmationsPage = () => {
    const translate = useTranslate();
    const { canAccess, isPending: accessPending } = useCanAccess({
        action: 'show',
        resource: 'dataconfirmations',
    });
    const [filter, setFilter] = useState<
        Omit<ConfirmationFilter, 'last_login_from'> & {
            last_login_from: string;
        }
    >(confirmationDefaults);
    const [allUsers, setAllUsers] = useState(false);
    const [role, setRole] = useState<ConfirmationRole | 'all'>('all');
    const valid =
        !!filter.from &&
        !!filter.to &&
        filter.from <= filter.to &&
        (allUsers || !!filter.last_login_from);
    const { data, isPending, error } = useDataConfirmations(
        {
            ...filter,
            last_login_from: allUsers ? null : filter.last_login_from,
        },
        canAccess === true && valid
    );
    if (accessPending) return <CircularProgress sx={{ m: 2 }} />;
    if (!canAccess)
        return (
            <Alert severity="error" sx={{ m: 2 }}>
                {translate('statistics.noaccess')}
            </Alert>
        );

    const roles = confirmationRoles.filter(
        value => role === 'all' || role === value
    );
    const dates =
        valid && data
            ? (filter.group_by === 'month'
                  ? eachMonthOfInterval
                  : eachDayOfInterval)({
                  start: parseISO(filter.from),
                  end: parseISO(filter.to),
              }).map(date =>
                  format(
                      date,
                      filter.group_by === 'month' ? 'yyyy-MM' : 'yyyy-MM-dd'
                  )
              )
            : [];
    const rows = dates.flatMap(date =>
        roles.map(value => {
            const item = data?.history.find(
                row => row.date === date && row.role === value
            );
            return {
                date,
                role: value,
                own: item?.own || 0,
                school: item?.school || 0,
            };
        })
    );
    const series = roles.flatMap(value => {
        const label = translate(`statistics.dataconfirmations.roles.${value}`);
        return [
            {
                id: `${value}-own`,
                label: `${label}: ${translate('statistics.dataconfirmations.own')}`,
                data: dates.map(
                    date =>
                        rows.find(
                            row => row.date === date && row.role === value
                        )?.own || 0
                ),
                showMark: false,
            },
            ...(value === 'teacher'
                ? [
                      {
                          id: 'teacher-school',
                          label: `${label}: ${translate('statistics.dataconfirmations.school')}`,
                          data: dates.map(
                              date =>
                                  rows.find(
                                      row =>
                                          row.date === date &&
                                          row.role === value
                                  )?.school || 0
                          ),
                          showMark: false,
                      },
                  ]
                : []),
        ];
    });

    return (
        <Box sx={{ p: 2 }}>
            <Title title={translate('statistics.dataconfirmations.title')} />
            <Typography variant="h6" gutterBottom>
                {translate('statistics.dataconfirmations.title')}
            </Typography>
            <Card sx={{ p: 2 }}>
                <Box
                    sx={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        alignItems: 'center',
                        gap: 1.5,
                        mb: 2,
                    }}
                >
                    <TextField
                        type="date"
                        size="small"
                        fullWidth={false}
                        sx={{ width: 160 }}
                        label={translate('statistics.activeusershistory.from')}
                        slotProps={{ inputLabel: { shrink: true } }}
                        value={filter.from}
                        onChange={event =>
                            setFilter(previous => ({
                                ...previous,
                                from: event.target.value,
                            }))
                        }
                    />
                    <TextField
                        type="date"
                        size="small"
                        fullWidth={false}
                        sx={{ width: 160 }}
                        label={translate('statistics.activeusershistory.to')}
                        slotProps={{ inputLabel: { shrink: true } }}
                        value={filter.to}
                        onChange={event =>
                            setFilter(previous => ({
                                ...previous,
                                to: event.target.value,
                            }))
                        }
                    />
                    <TextField
                        select
                        size="small"
                        fullWidth={false}
                        label={translate(
                            'statistics.dataconfirmations.group_by'
                        )}
                        value={filter.group_by}
                        onChange={event =>
                            setFilter(previous => ({
                                ...previous,
                                group_by:
                                    event.target.value === 'month'
                                        ? 'month'
                                        : 'day',
                            }))
                        }
                        sx={{ width: 140 }}
                    >
                        <MenuItem value="day">
                            {translate('statistics.dataconfirmations.day')}
                        </MenuItem>
                        <MenuItem value="month">
                            {translate('statistics.dataconfirmations.month')}
                        </MenuItem>
                    </TextField>
                    <TextField
                        select
                        size="small"
                        fullWidth={false}
                        label={translate('statistics.dataconfirmations.role')}
                        value={role}
                        onChange={event => {
                            const value = event.target.value;
                            if (
                                value === 'all' ||
                                value === 'teacher' ||
                                value === 'student' ||
                                value === 'pupil'
                            )
                                setRole(value);
                        }}
                        sx={{ width: 150 }}
                    >
                        <MenuItem value="all">
                            {translate(
                                'statistics.dataconfirmations.all_roles'
                            )}
                        </MenuItem>
                        {confirmationRoles.map(value => (
                            <MenuItem key={value} value={value}>
                                {translate(
                                    `statistics.dataconfirmations.roles.${value}`
                                )}
                            </MenuItem>
                        ))}
                    </TextField>
                    <TextField
                        type="date"
                        size="small"
                        fullWidth={false}
                        sx={{ width: 180 }}
                        disabled={allUsers}
                        label={translate(
                            'statistics.dataconfirmations.last_login_from'
                        )}
                        slotProps={{ inputLabel: { shrink: true } }}
                        value={filter.last_login_from}
                        onChange={event =>
                            setFilter(previous => ({
                                ...previous,
                                last_login_from: event.target.value,
                            }))
                        }
                    />
                    <FormControlLabel
                        sx={{ m: 0 }}
                        label={translate(
                            'statistics.dataconfirmations.all_active'
                        )}
                        control={
                            <Checkbox
                                checked={allUsers}
                                onChange={event =>
                                    setAllUsers(event.target.checked)
                                }
                            />
                        }
                    />
                </Box>
                <Alert severity="info" sx={{ mb: 2 }}>
                    {translate(
                        'statistics.dataconfirmations.distribution_note'
                    )}
                </Alert>
                {!valid ? (
                    <Alert severity="warning">
                        {translate('statistics.activeusershistory.pickrange')}
                    </Alert>
                ) : isPending ? (
                    <CircularProgress />
                ) : error ? (
                    <Alert severity="error">{error.message}</Alert>
                ) : (
                    data && (
                        <>
                            <Typography variant="h6">
                                {translate(
                                    'statistics.dataconfirmations.current'
                                )}
                            </Typography>
                            <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{ mb: 1 }}
                            >
                                {translate(
                                    'statistics.dataconfirmations.current_note',
                                    { days: data.recheck_days }
                                )}
                            </Typography>
                            <ConfirmationSummary data={data} role={role} />
                            <Typography variant="h6" sx={{ mt: 3 }}>
                                {translate(
                                    'statistics.dataconfirmations.distribution'
                                )}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                {translate(
                                    'statistics.dataconfirmations.school_note'
                                )}
                            </Typography>
                            <Typography variant="body2" sx={{ mt: 1 }}>
                                {translate(
                                    'statistics.dataconfirmations.period_totals'
                                )}
                            </Typography>
                            <TableContainer>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>
                                                {translate(
                                                    'statistics.dataconfirmations.role'
                                                )}
                                            </TableCell>
                                            <TableCell align="right">
                                                {translate(
                                                    'statistics.dataconfirmations.own'
                                                )}
                                            </TableCell>
                                            <TableCell align="right">
                                                {translate(
                                                    'statistics.dataconfirmations.school'
                                                )}
                                            </TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {roles.map(value => {
                                            const summary = data.summary.find(
                                                item => item.role === value
                                            );
                                            return (
                                                <TableRow key={value}>
                                                    <TableCell>
                                                        {translate(
                                                            `statistics.dataconfirmations.roles.${value}`
                                                        )}
                                                    </TableCell>
                                                    <TableCell align="right">
                                                        {numberFormatter.format(
                                                            summary?.own_confirmed ||
                                                                0
                                                        )}{' '}
                                                        /{' '}
                                                        {numberFormatter.format(
                                                            summary?.total || 0
                                                        )}
                                                    </TableCell>
                                                    <TableCell align="right">
                                                        {value === 'teacher'
                                                            ? `${numberFormatter.format(summary?.school_confirmed || 0)} / ${numberFormatter.format(summary?.school_total || 0)}`
                                                            : '—'}
                                                    </TableCell>
                                                </TableRow>
                                            );
                                        })}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                            {dates.length === 0 ? (
                                <Alert severity="info" sx={{ mt: 2 }}>
                                    {translate('statistics.nodata')}
                                </Alert>
                            ) : (
                                <>
                                    <LineChart
                                        height={320}
                                        xAxis={[
                                            { data: dates, scaleType: 'point' },
                                        ]}
                                        series={series}
                                    />
                                    <TableContainer>
                                        <Table size="small">
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell>
                                                        {translate(
                                                            'statistics.activeusershistory.date'
                                                        )}
                                                    </TableCell>
                                                    <TableCell>
                                                        {translate(
                                                            'statistics.dataconfirmations.role'
                                                        )}
                                                    </TableCell>
                                                    <TableCell align="right">
                                                        {translate(
                                                            'statistics.dataconfirmations.own'
                                                        )}
                                                    </TableCell>
                                                    <TableCell align="right">
                                                        {translate(
                                                            'statistics.dataconfirmations.school'
                                                        )}
                                                    </TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {rows.map(row => (
                                                    <TableRow
                                                        key={`${row.date}-${row.role}`}
                                                    >
                                                        <TableCell>
                                                            {row.date}
                                                        </TableCell>
                                                        <TableCell>
                                                            {translate(
                                                                `statistics.dataconfirmations.roles.${row.role}`
                                                            )}
                                                        </TableCell>
                                                        <TableCell align="right">
                                                            {numberFormatter.format(
                                                                row.own
                                                            )}
                                                        </TableCell>
                                                        <TableCell align="right">
                                                            {row.role ===
                                                            'teacher'
                                                                ? numberFormatter.format(
                                                                      row.school
                                                                  )
                                                                : '—'}
                                                        </TableCell>
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                </>
                            )}
                        </>
                    )
                )}
            </Card>
        </Box>
    );
};

export default DataConfirmationsPage;
